import React, { memo, useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Spinner from "../../Spinner";
import MetricTrendChart from "../../charts/MetricTrendChart";
import CardiopulmonaryResultRow from "../rows/CardiopulmonaryResultRow";
import { useCardiopulmonaryResults } from "../../../hooks/results/useCardiopulmonaryResults";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_RESULTS_PAGE_SIZE,
  useInfiniteResultsPageSize,
} from "../../../hooks/results/useInfiniteResultsPageSize";
import { getValidatedMeasurementValue } from "../../../utils/results/validatedRanges";
import {
  formatDayMonth,
  getLocalDayKeyMs,
} from "../../../utils/results/recordingIdTime";
import { SHOW_FULL_RESULTS } from "../../../config/results";

export default memo(function CardiopulmonaryResults({ db, uid }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const [metric, setMetric] = useState("HR"); // "HR" | "RR"
  const [pageSize, setPageSize] = useState(DEFAULT_RESULTS_PAGE_SIZE);

  const resetKey = `${uid || ""}:cardiopulmonary`;

  const { rows, loading, loadingMore, hasMore, error } =
    useCardiopulmonaryResults(db, uid, {
      pageSize,
    });

  const { handleScroll } = useInfiniteResultsPageSize({
    canLoadMore: hasMore,
    loading,
    loadingMore,
    rowsLength: rows.length,
    resetKey,
    pageSize,
    setPageSize,
    initialPageSize: DEFAULT_RESULTS_PAGE_SIZE,
  });

  const onRowClick = useCallback(
    (row) => {
      const variant = "RecordingTidalBreathing";
      const docid = row?.id;
      if (!docid) return;

      const qs =
        `variant=${encodeURIComponent(variant)}` +
        `&docid=${encodeURIComponent(docid)}` +
        `&context=${encodeURIComponent("cardiopulmonary")}`;

      navigate(`/result?${qs}`);
    },
    [navigate],
  );

  const trend = useMemo(() => {
    if (!Array.isArray(rows) || !rows.length) {
      return { labels: [], xValues: [], values: [], minMax: [], counts: [] };
    }

    const chron = [...rows].reverse();
    const samples = [];

    for (const r of chron) {
      const ms = r?.timestamp ? Date.parse(r.timestamp) : NaN;
      if (!Number.isFinite(ms)) continue;

      const value = getValidatedMeasurementValue(
        metric === "HR" ? "hr" : "rr",
        metric === "HR" ? r?.hr : r?.rr,
      );

      // skips null, "--", "", invalid numbers, and out-of-range values
      if (typeof value !== "number" || !Number.isFinite(value)) continue;

      samples.push({ ms, v: value });
    }

    const byDay = new Map();

    for (const s of samples) {
      const dayStartMs = getLocalDayKeyMs(s.ms, i18n.language);
      if (!Number.isFinite(dayStartMs)) continue;

      const cur = byDay.get(dayStartMs) || {
        sum: 0,
        n: 0,
        min: s.v,
        max: s.v,
      };

      cur.sum += s.v;
      cur.n += 1;
      cur.min = Math.min(cur.min, s.v);
      cur.max = Math.max(cur.max, s.v);

      byDay.set(dayStartMs, cur);
    }

    const days = Array.from(byDay.entries())
      .map(([dayStartMs, agg]) => ({ dayStartMs, ...agg }))
      .sort((a, b) => a.dayStartMs - b.dayStartMs);

    const xValues = days.map((d) => d.dayStartMs);
    const values = days.map((d) => d.sum / d.n);
    const minMax = days.map((d) => ({ min: d.min, max: d.max }));
    const counts = days.map((d) => d.n);
    const labels = xValues.map((ms) => formatDayMonth(ms, i18n.language));

    return { labels, xValues, values, minMax, counts };
  }, [rows, metric, i18n.language]);

  if (!uid || loading) {
    return (
      <div className="min-h-64 flex items-center justify-center">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="muted-text">
        {t("results.failedLoad", "Failed to load results.")}
      </div>
    );
  }
  if (!rows.length) {
    return (
      <div className="muted-text">
        {t("results.emptyRecordPrompt", "No data yet")}
      </div>
    );
  }

  const yUnit = metric === "HR" ? "bpm" : "bpm";
  const yDecimals = 0;
  const lineLabel =
    metric === "HR"
      ? t("results.cardio.hrAverage", "HR (Avg)")
      : t("results.cardio.rrAverage", "RR (Avg)");

  return (
    <div className="w-full h-full min-h-0 flex flex-col gap-2">
      {SHOW_FULL_RESULTS && (
        <>
          <div className="w-full flex gap-2 pt-2">
            <button
              type="button"
              className={[
                "px-2 py-1 rounded-xl text-sm",
                metric === "HR"
                  ? "bg-primary text-greyscale-1"
                  : "bg-greyscale-1 text-primary",
              ].join(" ")}
              onClick={() => setMetric("HR")}
            >
              {t("results.cardio.hr", "HR")}
            </button>

            <button
              type="button"
              className={[
                "px-2 py-1 rounded-xl text-sm",
                metric === "RR"
                  ? "bg-primary text-greyscale-1"
                  : "bg-greyscale-1 text-primary",
              ].join(" ")}
              onClick={() => setMetric("RR")}
            >
              {t("results.cardio.rr", "RR")}
            </button>
          </div>

          <MetricTrendChart
            height={190}
            labels={trend?.labels}
            values={trend?.values}
            xValues={trend?.xValues}
            xTickFormat="dd/MM"
            lineLabel={lineLabel}
            yUnit={yUnit}
            yDecimals={yDecimals}
            showMinMaxBars={true}
            minMax={trend?.minMax}
            counts={trend?.counts}
            showTrendLine={true}
          />
        </>
      )}
      <div
        aria-label="cardiopulmonary results list"
        className="w-full flex-1 min-h-0 overflow-y-auto"
        onScroll={handleScroll}
      >
        {rows.map((r, idx) => (
          <CardiopulmonaryResultRow
            key={r.id ?? idx}
            time={r.time}
            date={r.date}
            rr={r.rr}
            hr={r.hr}
            rrQuality={r.rrQuality}
            hrQuality={r.hrQuality}
            onClick={() => onRowClick(r)}
          />
        ))}
        {loadingMore ? <Spinner size={24} className="py-4" /> : null}
      </div>
    </div>
  );
});
